import React from 'react';
import {
  Box,
  Typography,
  Button,
  Breadcrumbs as MuiBreadcrumbs,
  Link,
  Chip,
  Stack,
  Fade,
  Slide
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

function PageHeader({
  title,
  subtitle,
  breadcrumbs = [],
  actions = [],
  primaryAction,
  status,
  loading = false
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const defaultBreadcrumbs = [
    { label: 'Dashboard', path: '/dashboard' },
    ...breadcrumbs
  ];

  return (
    <Box sx={{ mb: 4 }}>
      {/* Breadcrumbs */}
      {defaultBreadcrumbs.length > 1 && (
        <Fade in timeout={200}>
          <MuiBreadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            sx={{ mb: 2 }}
          >
            {defaultBreadcrumbs.map((crumb, index) => {
              const isLast = index === defaultBreadcrumbs.length - 1;
              const isCurrent = location.pathname === crumb.path;
              
              return isLast || isCurrent ? (
                <Typography
                  key={crumb.path}
                  color="text.primary"
                  sx={{ fontWeight: 500 }}
                >
                  {crumb.label}
                </Typography>
              ) : (
                <Link
                  key={crumb.path}
                  color="inherit"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(crumb.path);
                  }}
                  sx={{
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                      color: 'primary.main'
                    }
                  }}
                >
                  {crumb.label}
                </Link>
              );
            })}
          </MuiBreadcrumbs>
        </Fade>
      )}

      {/* Header Content */}
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2
        }}
      >
        {/* Title Section */}
        <Slide direction="right" in timeout={300}>
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  lineHeight: 1.2
                }}
              >
                {title}
              </Typography>
              
              {status && (
                <Chip
                  label={status}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>
            
            {subtitle && (
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: 600 }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Slide>

        {/* Actions Section */}
        {(actions.length > 0 || primaryAction) && (
          <Fade in timeout={400}>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                flexShrink: 0,
                alignSelf: { xs: 'stretch', sm: 'center' }
              }}
            >
              {actions.map((action, index) => (
                <Button
                  key={index}
                  variant={action.variant || 'outlined'}
                  color={action.color || 'primary'}
                  size={action.size || 'medium'}
                  startIcon={action.icon}
                  onClick={action.onClick}
                  disabled={loading || action.disabled}
                  sx={{
                    whiteSpace: 'nowrap',
                    ...action.sx
                  }}
                >
                  {action.label}
                </Button>
              ))}
              
              {primaryAction && (
                <Button
                  variant="contained"
                  color="primary"
                  size="medium"
                  startIcon={primaryAction.icon || <AddIcon />}
                  onClick={primaryAction.onClick}
                  disabled={loading || primaryAction.disabled}
                  sx={{
                    whiteSpace: 'nowrap',
                    ...primaryAction.sx
                  }}
                >
                  {primaryAction.label}
                </Button>
              )}
            </Stack>
          </Fade>
        )}
      </Box>
    </Box>
  );
}

export default PageHeader;